import * as React from "react";

import type {
	BrandSettings,
	DomainSettings,
	LocalizationSettings,
	Settings,
	WhitelabelSettings,
} from "~mangomap-ui/types/settings";

export interface SettingsContextValue {
	settings: Settings;
	setSettings: (settings: Settings) => void;
}

export const defaultSettings: Settings = {
	primaryColor: "mango",
	direction: "ltr",
};

export const SettingsContext = React.createContext<SettingsContextValue>({
	settings: {},
	setSettings: () => {
		// noop
	},
});

export interface SettingsProviderProps {
	children: React.ReactNode;
	settings: Settings;
}

export function SettingsProvider({
	children,
	settings: initialSettings,
}: SettingsProviderProps): React.JSX.Element {
	const [settings, setSettings] = React.useState<Settings>(initialSettings);

	React.useEffect(() => {
		setSettings(initialSettings);
	}, [initialSettings]);

	return (
		<SettingsContext.Provider value={{ settings, setSettings }}>
			{children}
		</SettingsContext.Provider>
	);
}

export const SettingsConsumer = SettingsContext.Consumer;

export function useSettings(): SettingsContextValue {
	const context = React.useContext(SettingsContext);

	if (!context) {
		throw new Error("useSettings must be used within a SettingsProvider");
	}

	return context;
}

export function useBrandSettings(): {
	brandSettings: BrandSettings;
	setBrandSettings: (brandSettings: BrandSettings) => void;
} {
	const { settings, setSettings } = useSettings();

	const setBrandSettings = React.useCallback(
		(brandSettings: BrandSettings) => {
			setSettings({ ...settings, brand: brandSettings });
		},
		[settings, setSettings],
	);

	return {
		brandSettings: settings.brand ?? {},
		setBrandSettings,
	};
}

export function useLocalizationSettings(): {
	localizationSettings: LocalizationSettings;
	setLocalizationSettings: (localizationSettings: LocalizationSettings) => void;
} {
	const { settings, setSettings } = useSettings();

	const setLocalizationSettings = React.useCallback(
		(localizationSettings: LocalizationSettings) => {
			setSettings({ ...settings, localization: localizationSettings });
		},
		[settings, setSettings],
	);

	return {
		localizationSettings: settings.localization ?? {},
		setLocalizationSettings,
	};
}

export function useDomainSettings(): {
	domainSettings: DomainSettings;
	setDomainSettings: (domainSettings: DomainSettings) => void;
} {
	const { settings, setSettings } = useSettings();

	const setDomainSettings = React.useCallback(
		(domainSettings: DomainSettings) => {
			setSettings({ ...settings, domain: domainSettings });
		},
		[settings, setSettings],
	);

	return {
		domainSettings: settings.domain ?? {},
		setDomainSettings,
	};
}

export function useWhitelabelSettings(): {
	whitelabelSettings: WhitelabelSettings;
	setWhitelabelSettings: (whitelabelSettings: WhitelabelSettings) => void;
} {
	const { settings, setSettings } = useSettings();

	const setWhitelabelSettings = React.useCallback(
		(whitelabelSettings: WhitelabelSettings) => {
			setSettings({ ...settings, whitelabel: whitelabelSettings });
		},
		[settings, setSettings],
	);

	return {
		whitelabelSettings: settings.whitelabel ?? {},
		setWhitelabelSettings,
	};
}
